// React imports
import React, { useEffect, useState } from 'react';
import '../../../src/App';

// Third-party imports
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ScrollableContent } from 'src/components/ScrollableContent';
import {
  fetchProjectByEmail,
  fetchProjectByResourceEmail,
  grantProgramManagerAccess,
} from 'src/redux/features/projects/projectsSlice';
import en from 'src/messages/en.json';
import { TableLoader } from '../TimeSheet/components/TableLoader';
import UserListModal from 'src/components/UserListModal';
import { Link, useNavigate } from 'react-router-dom';
import { IProjectAccess } from 'src/models/ProjectAccess';
import {
  Card,
  Checkbox,
  CheckboxChangeEvent,
  Flex,
  Input,
  Spin,
  Table,
  Typography,
} from 'antd';

import AntDButton from 'src/components/Button';
import { EditOutlined } from '@ant-design/icons';
import { formatToastMsg } from 'src/utils/helpers';
import { setError } from 'src/redux/features/common/commonSlice';
const { Title } = Typography;

const ProjectAccess = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [value, setValue] = useState('');
  const { data: projects, loading: projectsLoading } = useSelector(
    fetchProjectByResourceEmail,
  );

  const [data, setData] = useState([]);
  const [filteredProjectList, setFilteredProjectList] = useState([]);

  const [openUserModal, setOpenUserModal] = useState(false);
  const [checkedList, setCheckedList] = useState(
    data?.map((project) => project.projectId),
  );
  const [isGrantAccess, setIsGrantAccess] = useState(false);
  const [selectedUserForGrantAccess, setSelectedUserForGrantAccess] =
    useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const handleGrantAccessTo = () => {
    setIsGrantAccess(true);
    setOpenUserModal(true);
  };
  const closeModal = () => {
    setOpenUserModal(false);
    setSelectedUserForGrantAccess('');
  };
  const handleSelect = (value) => {
    setSelectedUserForGrantAccess(value);
  };
  const handleGrantAccessConfirm = async () => {
    if (!selectedUserForGrantAccess) {
      dispatch(setError(en.Access.Error.selectResourceGrantAccess));
      setOpenUserModal(true);

      return;
    }
    // Check if the selected user already has access to any of the selected projects
    const hasAccess = checkedList.find((projectId) => {
      const project = data.find((p) => p.projectId === projectId);

      if (project) {
        const duplicates = project.programManagers.find(
          (manager) => manager.resourceEmail === selectedUserForGrantAccess,
        );

        return duplicates;
      }

      return false;
    });

    if (hasAccess) {
      dispatch(
        setError(
          formatToastMsg(en.Access.Error.userAlreadyAdded, {
            selectedUser: selectedUserForGrantAccess,
          }),
        ),
      );
      setOpenUserModal(true);

      return;
    }

    setIsSaving(true);
    setOpenUserModal(false);

    const requestBodyData = {
      projectId: checkedList,
      itProjectLeaderEmail: auth.user.profile.email,
      resourceEmail: selectedUserForGrantAccess,
    };

    try {
      // Dispatch the thunk with the correct payload
      const response = await dispatch(
        grantProgramManagerAccess(requestBodyData),
      ).unwrap();

      setCheckedList([]);
      setData(response);
      setSelectedUserForGrantAccess('');

      toast.success(en.Access.Success.accessGranted);
    } catch (error) {
      dispatch(setError(en.Access.Error.grantAccessFailed));
    } finally {
      setIsSaving(false);
    }
  };

  const checkAll = data.length === checkedList.length;
  const handleSelectAllChange = (e: CheckboxChangeEvent) => {
    setCheckedList(
      e.target.checked ? data.map((project) => project.projectId) : [],
    );
  };

  const handleCheckboxChange = (id: number) => {
    setCheckedList((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleEditProject = (Item: IProjectAccess) => {
    navigate(`/project-access/${Item?.projectId}`);
  };

  useEffect(() => {
    if (value.trim() === '') {
      setFilteredProjectList(data);
    } else {
      const filtered = data.filter((project) =>
        project.projectName.toLowerCase().includes(value.toLowerCase()),
      );

      setFilteredProjectList(filtered);
    }
  }, [value, data]);

  const columns = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={checkAll}
            disabled={!filteredProjectList.length}
            onChange={(e) => {
              handleSelectAllChange(e);
            }}
          />
          <span style={{ marginLeft: '1rem' }}>{en.Access.Title.project}</span>
        </div>
      ),
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={checkedList.includes(record.projectId)}
            onChange={() => handleCheckboxChange(record.projectId)}
          />
          <span style={{ marginLeft: '1rem' }}>{record.projectName}</span>
        </div>
      ),
      width: '50%',
    },
    {
      title: en.Access.Title.noOfUsers,
      dataIndex: 'programManagers',
      key: 'programManagers',
      render: (programManagers) =>
        programManagers?.length
          ? `${programManagers.length} Users`
          : 'No Users',
      width: '40%',
    },
    {
      title: '',
      key: 'edit',
      render: (_, record) => (
        <Link to={`/project-access/${record.projectId}`}>
          <AntDButton
            type="link"
            prefixIcon={<EditOutlined />}
            onClick={() => handleEditProject(record)}
            htmlType="button"
            aria-label="Edit project"
          />
        </Link>
      ),
      width: '10%',
    },
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await dispatch(
          fetchProjectByEmail({
            ITProjectLeaderEmail: auth.user.profile.email,
          }),
        ).unwrap();

        setData(response);
      } catch (error) {
        dispatch(setError(en.Common.Error.fetchData));
      }
      setIsDataLoading(false);
    };

    fetchProjects();
  }, [dispatch, auth.user.profile.email]);
  useEffect(() => {
    return () => {
      setCheckedList([]);
    };
  }, []);

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {en.Access.Title.editAccess}
      </Title>
      <Flex gap={'middle'} vertical>
        <Card size="small">
          <Flex gap={'middle'} justify="space-between">
            <Typography.Text>
              {en.Access.Title.accessConfiguration}
            </Typography.Text>
          </Flex>
        </Card>
        <Card size="small">
          <Flex gap={'middle'} vertical>
            <Flex justify="flex-start">
              <Input
                type="text"
                placeholder={en.Common.Placeholder.searchHere}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{
                  width: '20rem',
                }}
                allowClear
              />
            </Flex>
            <ScrollableContent
              minHeight="calc(100vh - 18.75rem)"
              maxHeight="calc(100vh - 18.75rem)"
            >
              {projectsLoading || isDataLoading ? (
                <Spin />
              ) : (
                <Table
                  columns={columns}
                  dataSource={filteredProjectList}
                  rowKey={(record) => record.projectId}
                  pagination={false}
                  loading={isDataLoading}
                />
              )}
            </ScrollableContent>
            <Flex gap={'middle'} justify="flex-end">
              <AntDButton
                onClick={handleGrantAccessTo}
                disabled={!checkedList.length}
                type="primary"
                htmlType="button"
                statusOnComplete="Grant Access To"
              />
            </Flex>
            <UserListModal
              show={openUserModal}
              onHide={closeModal}
              handleSelect={handleSelect}
              onConfirm={handleGrantAccessConfirm}
              selectedUser={selectedUserForGrantAccess}
            />
          </Flex>
        </Card>
      </Flex>
    </>
  );
};

export default ProjectAccess;
